import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { getFederateToken, getUserRolesFromFederate, getUserNameFromFederate, getUserLoginFromFederate } from '../auth/federateAuth';
import { APP_CONFIG } from '../config/authConfig';
import Spinner from '@amzn/awsui-components-console/spinner';

interface FederateContextType {
  token: string | null;
  userName: string | null;
  userLogin: string | null;
  userRoles: string[];
  loading: boolean;
  error: string | null;
}

const FederateContext = createContext<FederateContextType | undefined>(undefined);

interface FederateProviderProps {
  children: ReactNode;
}

export const FederateProvider: React.FC<FederateProviderProps> = ({ children }) => {
  const [token, setToken] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [userLogin, setUserLogin] = useState<string | null>(null);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeFederate = async () => {
      try {
        const federateToken = await getFederateToken(APP_CONFIG);
        const roles = getUserRolesFromFederate(federateToken);
        const name = getUserNameFromFederate(federateToken);
        const login = getUserLoginFromFederate(federateToken);

        setToken(federateToken);
        setUserRoles(roles);
        setUserName(name);
        setUserLogin(login);
        setError(null);
        setLoading(false);
      } catch (err) {
        // User not authenticated with federate
        console.log('No federate authentication:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to get federate token';
        setError(errorMessage);
      }
    };

    initializeFederate();
  }, []);

  // Show loading state while initializing authentication
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spinner size="large" />
      </div>
    );
  }

  return <FederateContext.Provider value={{ token, userName, userLogin, userRoles, loading, error }}>{children}</FederateContext.Provider>;
};

export const useFederate = () => {
  const context = useContext(FederateContext);
  if (context === undefined) {
    throw new Error('useFederate must be used within a FederateProvider');
  }
  return context;
};

/** Federate session, or undefined outside a FederateProvider (no throw). */
export const useOptionalFederate = (): FederateContextType | undefined => useContext(FederateContext);
